import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { StriimButton, StriimTypography, StriimModal, useStriimForm, StriimCircularProgress } from "@striim/striim-ui";
import { TestConnectionStatus } from "src/generic/test-connection/test-connection";
import { styles } from "./add-queries-modal.styles";
import QuerySidebar from "./query-sidebar";
import QueryEditor from "./query-editor";
import api from "core/api/api";
import growl from "app/components/common/growl";
import { isValidIdentifier } from "../../select-tables-modal/select-tables.utils";
import { createNewQuery, QueryItem } from "./add-queries-modal.utils";
import type { QueryPreviewResponse, QueryPreviewState } from "../query-preview/query-preview";
import EmptyIllustration from "app/images/striimline/empty.svg";

export type { QueryItem };

const QUERY_PREVIEW_ROW_LIMIT = 10;

const sleepFn = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const MAX_IDENTIFIER_LENGTH = 128;

interface ValidateQueryResponse {
    valid: boolean;
    query: string;
    errorType?: string;
    error?: string;
}

interface AddQueriesModalProps {
    metaObjectId?: string;
    isVisible: boolean;
    onCancel: () => void;
    onSave: (queries: QueryItem[]) => void;
    initialQueries?: QueryItem[];
    testConnectionStatus?: TestConnectionStatus;
    adapterName: string;
    databaseProviderType?: string;
    isCacheComponent?: boolean;
}

const AddQueriesModal: React.FC<AddQueriesModalProps> = ({
    metaObjectId,
    isVisible,
    onCancel,
    onSave,
    initialQueries = [],
    adapterName,
    databaseProviderType,
    isCacheComponent
}) => {
    const queryCounterRef = useRef<number>(1);
    const [queries, setQueries] = useState<QueryItem[]>(() => {
        if (initialQueries.length > 0) return [...initialQueries];
        const { query, nextCounter } = createNewQuery([], queryCounterRef.current);
        queryCounterRef.current = nextCounter;
        return [query];
    });
    const [selectedQueryId, setSelectedQueryId] = useState<string>(() =>
        initialQueries.length > 0 ? initialQueries[0].id : queries[0].id
    );
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [queryErrors, setQueryErrors] = useState<Record<string, string>>({});
    const [dismissedErrors, setDismissedErrors] = useState<Set<string>>(new Set());
    const [validatedQueries, setValidatedQueries] = useState<Set<string>>(new Set());
    const [isValidating, setIsValidating] = useState(false);
    const { values: formValues } = useStriimForm();
    const [isInitialValidating, setIsInitialValidating] = useState(false);
    const hasRunInitialValidation = useRef(false);
    const [emptyQueryErrorType, setEmptyQueryErrorType] = useState<"save" | "add" | null>(null);
    const [previewStates, setPreviewStates] = useState<Record<string, QueryPreviewState>>({});
    const [isEmptyState, setIsEmptyState] = useState(false);

    useEffect(() => {
        if (hasRunInitialValidation.current || initialQueries.length === 0) return;
        hasRunInitialValidation.current = true;

        (async () => {
            setIsInitialValidating(true);
            try {
                const properties: Record<string, string> = {};
                Object.keys(formValues).forEach(key => {
                    if (formValues[key] != null) {
                        properties[key] = formValues[key];
                    }
                });
                const querySqls = initialQueries.map(q => q.sql);
                const response = await api.validateSelectQueries(querySqls, properties);
                const results: ValidateQueryResponse[] = JSON.parse(response);

                const newErrors: Record<string, string> = {};
                const newValidated = new Set<string>();

                results.forEach((result, index) => {
                    const query = initialQueries[index];
                    if (!query) return;
                    if (!result.valid) {
                        newErrors[query.id] = result.error || "Validation failed";
                    } else {
                        newValidated.add(query.id);
                    }
                });

                setQueryErrors(newErrors);
                setValidatedQueries(newValidated);
            } catch (error) {
                console.error(error);
            } finally {
                setIsInitialValidating(false);
            }
        })();
    }, []);

    const selectedQuery = useMemo(() => queries.find(q => q.id === selectedQueryId) || queries[0], [
        queries,
        selectedQueryId
    ]);

    const sortedQueries = useMemo(() => [...queries].sort((a, b) => a.priority - b.priority), [queries]);
    const emptyQueryIds = useMemo(
        () => (emptyQueryErrorType ? new Set(queries.filter(q => !q.sql?.trim()).map(q => q.id)) : new Set<string>()),
        [queries, emptyQueryErrorType]
    );
    const duplicateIdentifiers = useMemo(() => {
        return new Set(
            queries
                .filter((q, _, arr) => arr.some(other => other.id !== q.id && other.identifier === q.identifier))
                .map(q => q.identifier)
        );
    }, [queries]);

    const {
        hasDuplicateIdentifiers,
        hasEmptySql,
        hasEmptyIdentifier,
        hasInvalidIdentifier,
        hasTooLongIdentifier,
        nonEmptyQueryCount
    } = useMemo(
        () => ({
            hasDuplicateIdentifiers: duplicateIdentifiers.size > 0,
            hasEmptySql: queries.some(q => !q.sql?.trim()),
            hasEmptyIdentifier: queries.some(q => !q.identifier?.trim()),
            hasInvalidIdentifier: queries.some(q => !isValidIdentifier(q.identifier)),
            hasTooLongIdentifier: queries.some(q => (q.identifier?.length ?? 0) > MAX_IDENTIFIER_LENGTH),
            nonEmptyQueryCount: queries.filter(q => q.sql?.trim()).length
        }),
        [queries, duplicateIdentifiers]
    );

    const handleAddQuery = useCallback(() => {
        if (isEmptyState) {
            const { query: newQuery, nextCounter } = createNewQuery([], queryCounterRef.current);
            queryCounterRef.current = nextCounter;
            setQueries([newQuery]);
            setSelectedQueryId(newQuery.id);
            setIsEmptyState(false);
            setEmptyQueryErrorType(null);
            return;
        }
        if (hasEmptySql) {
            setEmptyQueryErrorType("add");
            return;
        }
        setEmptyQueryErrorType(null);
        const { query: newQuery, nextCounter } = createNewQuery(queries, queryCounterRef.current);
        queryCounterRef.current = nextCounter;
        setQueries(prev => [...prev, newQuery]);
        setSelectedQueryId(newQuery.id);
    }, [queries, hasEmptySql, isEmptyState]);

    const handleDeleteQuery = useCallback(
        (id: string) => {
            setPreviewStates(prev => {
                if (!(id in prev)) return prev;
                const updated = { ...prev };
                delete updated[id];
                return updated;
            });
            if (queries.length <= 1) {
                setQueries([]);
                setSelectedQueryId("");
                setEmptyQueryErrorType(null);
                setQueryErrors({});
                setValidatedQueries(new Set());
                setDismissedErrors(new Set());
                setIsEmptyState(true);
                return;
            }
            const filtered = queries.filter(q => q.id !== id);
            const sorted = [...filtered].sort((a, b) => a.priority - b.priority);
            const updated = sorted.map((q, index) => ({ ...q, priority: index + 1 }));
            setQueries(updated);
            if (selectedQueryId === id) {
                setSelectedQueryId(updated[0].id);
            }
            const remainingHasEmpty = updated.some(q => !q.sql?.trim());
            if (!remainingHasEmpty || updated.length <= 1) {
                setEmptyQueryErrorType(null);
            }
        },
        [queries, selectedQueryId]
    );

    const handleUpdateQuery = useCallback((id: string, field: keyof QueryItem, value: string | number) => {
        setQueries(prev => prev.map(q => (q.id === id ? { ...q, [field]: value } : q)));
    }, []);

    const handleDragEnd = useCallback(
        (result: any) => {
            if (!result.destination) return;

            const reordered = [...sortedQueries];
            const [moved] = reordered.splice(result.source.index, 1);
            reordered.splice(result.destination.index, 0, moved);

            const updated = reordered.map((q, index) => ({
                ...q,
                priority: index + 1
            }));
            setQueries(updated);
        },
        [sortedQueries]
    );

    const handleSqlChange = useCallback(
        (value: string) => {
            if (selectedQuery) {
                handleUpdateQuery(selectedQuery.id, "sql", value);
                if (value.trim() && emptyQueryErrorType) {
                    setEmptyQueryErrorType(null);
                }
                if (queryErrors[selectedQuery.id]) {
                    setQueryErrors(prev => {
                        const next = { ...prev };
                        delete next[selectedQuery.id];
                        return next;
                    });
                    setDismissedErrors(prev => {
                        const next = new Set(prev);
                        next.delete(selectedQuery.id);
                        return next;
                    });
                }
                if (validatedQueries.has(selectedQuery.id)) {
                    setValidatedQueries(prev => {
                        const next = new Set(prev);
                        next.delete(selectedQuery.id);
                        return next;
                    });
                }
            }
        },
        [selectedQuery, handleUpdateQuery, queryErrors, validatedQueries, emptyQueryErrorType]
    );

    const handleValidateQuery = useCallback(async () => {
        const queryId = selectedQuery.id;
        setIsValidating(true);
        // show loader for half sec
        await sleepFn(500);
        setQueryErrors(prev => {
            const next = { ...prev };
            delete next[queryId];
            return next;
        });
        setDismissedErrors(prev => {
            const next = new Set(prev);
            next.delete(queryId);
            return next;
        });
        setValidatedQueries(prev => {
            const next = new Set(prev);
            next.delete(queryId);
            return next;
        });
        try {
            const properties: Record<string, string> = {};
            Object.keys(formValues).forEach(key => {
                if (formValues[key] != null) {
                    properties[key] = formValues[key]?.value ?? formValues[key];
                }
            });
            const response = await api.validateSelectQuery(selectedQuery.sql, properties);
            const result: ValidateQueryResponse = JSON.parse(response);
            if (!result.valid) {
                setQueryErrors(prev => ({
                    ...prev,
                    [queryId]: result.error || "Validation failed"
                }));
            } else {
                setValidatedQueries(prev => {
                    const next = new Set(prev);
                    next.add(queryId);
                    return next;
                });
            }
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Validation failed";
            setQueryErrors(prev => ({
                ...prev,
                [queryId]: errorMessage
            }));
            growl.error(errorMessage);
        } finally {
            setIsValidating(false);
        }
    }, [selectedQuery, formValues]);

    const handlePreviewQuery = useCallback(async () => {
        if (!selectedQuery) return;
        const queryId = selectedQuery.id;
        const sql = selectedQuery.sql;
        if (!sql?.trim()) return;

        setPreviewStates(prev => ({
            ...prev,
            [queryId]: { isLoading: true, data: null, error: null, previewedSql: sql }
        }));

        const properties: Record<string, string> = {};
        Object.keys(formValues).forEach(key => {
            if (formValues[key] != null) {
                properties[key] = formValues[key]?.value ?? formValues[key];
            }
        });

        try {
            const response = await api.getQueryResult(
                sql,
                properties,
                QUERY_PREVIEW_ROW_LIMIT,
                metaObjectId,
                adapterName
            );
            const data: QueryPreviewResponse = JSON.parse(response);
            setPreviewStates(prev => ({
                ...prev,
                [queryId]: data.success
                    ? { isLoading: false, data, error: null, previewedSql: sql }
                    : {
                        isLoading: false,
                        data: null,
                        error: data.errorMessage || "Query Preview failed",
                        previewedSql: sql
                    }
            }));
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : "Query Preview failed";
            setPreviewStates(prev => ({
                ...prev,
                [queryId]: { isLoading: false, data: null, error: errorMessage, previewedSql: sql }
            }));
            growl.error(errorMessage);
        }
    }, [selectedQuery, formValues, metaObjectId, adapterName]);

    return (
        <>
            <StriimModal
                autoHeight
                size="large"
                isVisible={isVisible}
                titleContent={
                    <StriimTypography variant="h2" color="primary.700" data-test-id="add-queries-modal-title">
                        {isCacheComponent ? "Query" : adapterName === "DatabaseReader" ? "Fast Snapshot Load using SQL Queries" : "Queries"}
                    </StriimTypography>
                }
                headerBorder={true}
                footerBorder={true}
                footerContent={
                    <Grid container justifyContent={"space-between"} alignItems={"center"}>
                        {nonEmptyQueryCount > 0 ? (
                            <StriimTypography
                                variant="body4"
                                color={"greyscale.800"}
                                fontFamily={"Inter"}
                                data-test-id="queries-counter"
                            >
                                {nonEmptyQueryCount} quer{nonEmptyQueryCount > 1 ? "ies" : "y"} added.
                            </StriimTypography>
                        ) : null}
                        <Grid item display={"flex"} alignItems={"center"} gap={1} sx={{ ml: "auto" }}>
                            <StriimButton
                                variant="text"
                                onClick={() => {
                                    if (isEmptyState) {
                                        onSave([]);
                                        return;
                                    }
                                    onCancel();
                                }}
                                data-testid="cancel-button"
                            >
                                Cancel
                            </StriimButton>
                            <StriimButton
                                variant="primary"
                                disabled={
                                    isEmptyState ||
                                    hasDuplicateIdentifiers ||
                                    hasEmptyIdentifier ||
                                    hasInvalidIdentifier ||
                                    hasTooLongIdentifier
                                }
                                onClick={() => {
                                    if (hasEmptySql) {
                                        setEmptyQueryErrorType("save");
                                        return;
                                    }
                                    onSave(sortedQueries);
                                }}
                                data-testid="save-queries-button"
                            >
                                Save Queries
                            </StriimButton>
                        </Grid>
                    </Grid>
                }
                dialogProps={
                    {
                        "data-test-id": "add-queries-modal",
                        sx: styles.dialog
                    } as any
                }
            >
                {isInitialValidating ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                        minHeight={400}
                        data-testid="queries-loading"
                    >
                        <StriimCircularProgress size={32} />
                        <StriimTypography variant="body4" color="greyscale.700" mt={1.5}>
                            Loading
                        </StriimTypography>
                    </Box>
                ) : isEmptyState ? (
                    <Box sx={styles.emptyState}>
                        <EmptyIllustration style={styles.emptyStateImage} />
                        <StriimTypography variant="body4" color="greyscale.700" sx={styles.emptyStateText}>
                            No Queries to Display. Add a query to get started.
                        </StriimTypography>
                        <StriimButton variant="text" onClick={handleAddQuery}>
                            + Add Query
                        </StriimButton>
                    </Box>
                ) : (
                    <Grid container display="flex" flexWrap="nowrap" alignItems="stretch" height={"620px"} data-testid="queries-main-content">
                        <QuerySidebar
                            sortedQueries={sortedQueries}
                            queries={queries}
                            selectedQueryId={selectedQueryId}
                            queryErrors={queryErrors}
                            validatedQueries={validatedQueries}
                            emptyQueryIds={emptyQueryIds}
                            isCacheComponent={isCacheComponent}
                            onSelect={id => setSelectedQueryId(id)}
                            onDragEnd={handleDragEnd}
                            onAdd={handleAddQuery}
                            onRequestDelete={setDeleteConfirmId}
                        />

                        {selectedQuery && (
                            <QueryEditor
                                selectedQuery={selectedQuery}
                                queryErrors={queryErrors}
                                dismissedErrors={dismissedErrors}
                                duplicateIdentifiers={duplicateIdentifiers}
                                isValidating={isValidating}
                                emptyQueryErrorType={emptyQueryErrorType}
                                adapterName={adapterName}
                                databaseProviderType={databaseProviderType}
                                previewState={previewStates[selectedQuery.id]}
                                onUpdateQuery={handleUpdateQuery}
                                onSqlChange={handleSqlChange}
                                onValidateQuery={handleValidateQuery}
                                onPreviewQuery={handlePreviewQuery}
                                onToggleDismissError={(queryId, dismissed) =>
                                    setDismissedErrors(prev => {
                                        const next = new Set(prev);
                                        dismissed ? next.add(queryId) : next.delete(queryId);
                                        return next;
                                    })
                                }
                                onDismissEmptyQueryError={() => setEmptyQueryErrorType(null)}
                                data-test-id="query-editor"
                            />
                        )}
                    </Grid>
                )}
            </StriimModal>
            <StriimModal
                size="small"
                isVisible={!!deleteConfirmId}
                titleContent={
                    <StriimTypography variant="h2" color="primary.700" data-test-id="delete-query-modal-title">
                        Delete Query
                    </StriimTypography>
                }
                onCancel={() => setDeleteConfirmId(null)}
                onConfirm={() => {
                    if (deleteConfirmId) {
                        handleDeleteQuery(deleteConfirmId);
                    }
                    setDeleteConfirmId(null);
                }}
                cancelButtonProps={
                    {
                        "data-test-id": "cancel-delete-query-button"
                    } as any
                }
                confirmButtonProps={
                    {
                        "data-test-id": "confirm-delete-query-button"
                    } as any
                }
                cancelContent="Cancel"
                confirmContent="Delete"
                variant="error"
                dialogProps={
                    {
                        "data-test-id": "delete-query-modal"
                    } as any
                }
            >
                <StriimTypography variant="body4" color="greyscale.900" data-test-id="delete-query-confirmation-text">
                    Are you sure you want to delete this query? This action cannot be undone.
                </StriimTypography>
            </StriimModal>
        </>
    );
};

export default AddQueriesModal;
